import React, { useState } from 'react';
import { Plus, X, ChevronDown } from 'lucide-react';
import { useAssumptionDraftStore, CUSTOM_CAPEX_OPTIONS, type CapExItem } from '../../store/useAssumptionDraftStore';
import { formatCurrency } from '../../utils/formatters';

interface CapExEditorProps {
  totalUnits: number;
  ssotCapexTotal?: number;
}

export const CapExEditor: React.FC<CapExEditorProps> = ({ totalUnits, ssotCapexTotal }) => {
  const { draft, updateCapexItem, addCapexItem, removeCapexItem } = useAssumptionDraftStore();
  const [showAddMenu, setShowAddMenu] = useState(false);

  const items = draft.capexItems;
  const subtotal = items.reduce((sum, item) => sum + item.costPerUnit * totalUnits, 0);
  const contingency = subtotal * draft.contingencyRate;
  const total = subtotal + contingency;

  // Filter out items already in the list for the add menu
  const existingNames = new Set(items.map(i => i.name));
  const availableCustom = CUSTOM_CAPEX_OPTIONS.filter(n => !existingNames.has(n));

  const handleCostChange = (item: CapExItem, rawValue: string) => {
    const num = parseInt(rawValue.replace(/[^0-9]/g, ''), 10);
    updateCapexItem(item.id, isNaN(num) ? 0 : num);
  };

  const roiDelta = ssotCapexTotal != null && ssotCapexTotal > 0
    ? ((total - ssotCapexTotal) / ssotCapexTotal) * 100
    : null;

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="grid grid-cols-[1fr_100px_40px_100px] gap-2 text-sm text-cream/60 font-medium px-1">
        <span>Item</span>
        <span className="text-right">$/Unit</span>
        <span className="text-center">&times;</span>
        <span className="text-right">Total</span>
      </div>

      {/* Line items */}
      {items.map((item) => (
        <div
          key={item.id}
          className="grid grid-cols-[1fr_100px_40px_100px_28px] gap-2 items-center"
        >
          <span className="text-sm text-ivory truncate">
            {item.name}
            {item.isCustom && (
              <span className="ml-1.5 text-sm text-gold/60 uppercase tracking-wider">custom</span>
            )}
          </span>
          <input
            type="text"
            value={`$${item.costPerUnit.toLocaleString()}`}
            onChange={(e) => handleCostChange(item, e.target.value)}
            className="text-right text-sm bg-obsidian border border-slate-dark rounded px-2 py-1 text-ivory focus:border-gold focus:outline-none w-full"
          />
          <span className="text-center text-sm text-cream/40">&times;{totalUnits}</span>
          <span className="text-right text-sm text-ivory font-mono">
            {formatCurrency(item.costPerUnit * totalUnits)}
          </span>
          {item.isCustom ? (
            <button
              onClick={() => removeCapexItem(item.id)}
              className="w-6 h-6 flex items-center justify-center text-crimson/60 hover:text-crimson transition-colors rounded hover:bg-crimson/10"
              title="Remove item"
            >
              <X size={14} />
            </button>
          ) : (
            <div className="w-6" />
          )}
        </div>
      ))}

      {/* Add custom item */}
      <div className="relative">
        <button
          onClick={() => setShowAddMenu(!showAddMenu)}
          className="flex items-center gap-1.5 text-sm text-gold hover:text-gold-bright transition-colors px-1 py-1"
        >
          <Plus size={14} />
          Add Item
          <ChevronDown size={12} className={`transition-transform ${showAddMenu ? 'rotate-180' : ''}`} />
        </button>

        {showAddMenu && (
          <div className="absolute left-0 top-full mt-1 z-10 bg-charcoal border border-slate-dark rounded-lg shadow-lg py-1 min-w-[180px]">
            {availableCustom.length > 0 ? (
              availableCustom.map((name) => (
                <button
                  key={name}
                  onClick={() => {
                    addCapexItem(name, 0);
                    setShowAddMenu(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-ivory hover:bg-obsidian hover:text-gold transition-colors"
                >
                  {name}
                </button>
              ))
            ) : (
              <div className="px-3 py-1.5 text-sm text-cream/40">All items added</div>
            )}
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-slate-dark" />

      {/* Contingency */}
      <div className="grid grid-cols-[1fr_100px_40px_100px_28px] gap-2 items-center">
        <span className="text-sm text-cream/60">
          Contingency ({(draft.contingencyRate * 100).toFixed(0)}%)
        </span>
        <span />
        <span />
        <span className="text-right text-sm text-ivory font-mono">
          {formatCurrency(contingency)}
        </span>
        <div className="w-6" />
      </div>

      {/* Total */}
      <div className="grid grid-cols-[1fr_100px_40px_100px_28px] gap-2 items-center bg-obsidian rounded-lg px-2 py-2">
        <span className="text-sm font-semibold text-gold">Total CapEx</span>
        <span />
        <span />
        <span className="text-right text-sm font-semibold text-gold font-mono">
          {formatCurrency(total)}
        </span>
        <div className="w-6" />
      </div>

      {/* ROI benchmark comparison */}
      {roiDelta !== null && (
        <div className="flex items-center gap-2 px-1 py-1">
          <span className="text-sm text-cream/50">vs SSOT Budget:</span>
          <span className={`text-sm font-medium ${
            Math.abs(roiDelta) < 5 ? 'text-emerald' : roiDelta > 0 ? 'text-crimson' : 'text-gold'
          }`}>
            {roiDelta > 0 ? '+' : ''}{roiDelta.toFixed(1)}%
          </span>
          <span className="text-sm text-cream/40">
            (SSOT: {formatCurrency(ssotCapexTotal)})
          </span>
        </div>
      )}
    </div>
  );
};
